import React from "react";

export default function OrderCard({
  order,
  onClick,
  selected = false,
  onSelectChange,
  onDelete,
}) {
  // Helpers
  const toDateSafe = (ts) => {
    if (!ts) return null;
    // Native Date instance
    if (ts instanceof Date) return ts;
    // Firestore Timestamp
    if (typeof ts?.toDate === "function") {
      try {
        return ts.toDate();
      } catch (_) {
        /* ignore */
      }
    }
    // Firestore-like object {seconds, nanoseconds}
    if (
      typeof ts === "object" &&
      ts !== null &&
      typeof ts.seconds === "number"
    ) {
      return new Date(ts.seconds * 1000);
    }
    // Numeric epoch (allow seconds or ms)
    if (typeof ts === "number") {
      const ms = ts < 1e12 ? ts * 1000 : ts; // treat small numbers as seconds
      return new Date(ms);
    }
    // ISO string or other date-compatible string
    if (typeof ts === "string") return new Date(ts);
    return null;
  };
  const fmtDate = (ts) => {
    const d = toDateSafe(ts);
    if (!(d instanceof Date) || isNaN(d)) return "—";
    const dtf = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const s = dtf.format(d); // e.g., "11 Feb 2024"
    return s.replace(/ (\d{4})$/, ", $1"); // -> "11 Feb, 2024"
  };
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        })
      : v != null
        ? String(v)
        : "—";
  };

  const parseQuantity = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return Math.max(1, Math.round(num));
  };

  const getEntryQuantity = (entry) => {
    if (!entry || typeof entry !== "object") return 1;
    const candidates = [entry.quantity, entry.count, entry.qty];
    for (const candidate of candidates) {
      const parsed = parseQuantity(candidate);
      if (parsed != null) return parsed;
    }
    return 1;
  };

  const sumQuantities = (list) => {
    if (!Array.isArray(list) || !list.length) return null;
    return list.reduce((total, entry) => total + getEntryQuantity(entry), 0);
  };

  // Derivations
  const idRaw = order?.id != null ? String(order.id) : "";
  const idShort = idRaw
    ? idRaw.length > 6
      ? `#${idRaw.slice(0, 4)}…${idRaw.slice(-2)}`
      : `#${idRaw}`
    : "—";
  const dateLabel = fmtDate(order?.createdAt);
  const customerName = order?.customerName || "Customer";
  const total =
    order?.total ?? order?.amount ?? order?.order_total ?? order?.price;
  const paid = order?.amountPaid ?? 0;
  const normalize = (value) =>
    typeof value === "string"
      ? value.toLowerCase().replace(/[\s_-]+/g, "")
      : "";
  const paymentStatusKey = normalize(order?.paymentStatus);
  let paymentLabel;
  let paymentKey;

  if (
    paymentStatusKey === "paid" ||
    order?.paymentStatus === true ||
    paymentStatusKey === "true"
  ) {
    paymentLabel = "Paid";
    paymentKey = "paid";
  } else if (paymentStatusKey === "sent") {
    paymentLabel = "Sent";
    paymentKey = "sent";
  } else if (paymentStatusKey === "unsent") {
    paymentLabel = "Unsent";
    paymentKey = "unsent";
  } else if (paymentStatusKey === "unpaid") {
    paymentLabel = "Unpaid";
    paymentKey = "unpaid";
  } else {
    const paymentMethod = normalize(order?.paymentTerms || order?.paymentType);
    const isStripePaid = paymentMethod === "stripe";
    const paymentSuccess = isStripePaid
      ? true
      : Number(total) > 0
        ? Number(paid) >= Number(total)
        : !!order?.paidInFull || normalize(order?.paymentStatus) === "success";
    paymentLabel = paymentSuccess ? "Paid" : "Unpaid";
    paymentKey = paymentSuccess ? "paid" : "unpaid";
  }
  const itemsCount =
    sumQuantities(order?.pictures) ??
    sumQuantities(order?.items) ??
    sumQuantities(order?.images) ??
    0;
  const itemsLabel = `${itemsCount}`;

  // Status mapping → colored pill
  const rawStatus = String(order?.status || "").trim();
  const norm = rawStatus.toLowerCase().replace(/[\s_-]+/g, "");
  const statusMap = {
    pending: "Pending",
    downloaded: "Downloaded",
    inprogress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  const statusKey =
    norm === "in-progress" || norm === "in_progress" ? "inprogress" : norm;
  const statusLabel = statusMap[statusKey] || "Pending";
  const statusClass = `op-pill--status-${statusKey in statusMap ? statusKey : "pending"}`;

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick && onClick(order);
    }
  };

  return (
    <div
      className="op-row"
      role="button"
      tabIndex={0}
      onClick={() => onClick && onClick(order)}
      onKeyDown={onKey}
    >
      <div className="op-cell op-cell--check">
        <input
          type="checkbox"
          aria-label="select row"
          checked={!!selected}
          onChange={(e) => onSelectChange && onSelectChange(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="op-cell op-cell--order">{idShort}</div>
      <div className="op-cell op-cell--date">{dateLabel}</div>
      <div className="op-cell op-cell--customer">{customerName}</div>

      <div className="op-cell op-cell--total">{money(total)}</div>
      <div className="op-cell op-cell--items">{itemsLabel}</div>
      <div className="op-cell op-cell--status">
        <span className={`op-pill ${statusClass}`}>{statusLabel}</span>
      </div>
      <div className="op-cell op-cell--payment">
        <span className={`op-pill op-pill--pay-${paymentKey}`}>
          {paymentLabel}
        </span>
      </div>
      <div className="op-cell op-cell--action">
        <button className="op-icon" aria-label="view">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              fill="currentColor"
              d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10z"
            />
          </svg>
        </button>

        <button
          className="op-icon"
          aria-label="delete order"
          onClick={(e) => {
            e.stopPropagation();
            onDelete && onDelete(order);
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              fill="currentColor"
              d="M9 3a1 1 0 00-1 1v1H4v2h1v11a2 2 0 002 2h10a2 2 0 002-2V7h1V5h-4V4a1 1 0 00-1-1H9zm1 2h4V5h-4v1zm-1 4h2v8H9V9zm4 0h2v8h-2V9z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
